import { useEffect, useState } from 'react'
import { useNotification } from '../contexts/NotificationContext'
import '../styles/service-mode.css'

function ServiceModeController({ apiService }) {
  const [serviceMode, setServiceMode] = useState('manual') // 'manual', 'ai-assisted', 'full-auto'
  const [isOnline, setIsOnline] = useState(false)
  const [aiAgentStatus, setAiAgentStatus] = useState('disconnected') // 'connected', 'disconnected', 'busy'
  const [autoTradingEnabled, setAutoTradingEnabled] = useState(false)
  const [lastActivity, setLastActivity] = useState(null)
  const { showNotification } = useNotification()

  // Check service status
  const checkServiceStatus = async () => {
    if (!apiService || !apiService.isAuthenticated()) return

    try {
      const healthResponse = await apiService.request('/health')
      setIsOnline(true)
      
      // Check AI agent status
      try {
        const aiStatus = await apiService.request('/api/ai/status')
        setAiAgentStatus(aiStatus.status || 'connected')
        setLastActivity(aiStatus.lastActivity)
      } catch (error) {
        setAiAgentStatus('disconnected')
      }
    } catch (error) {
      setIsOnline(false)
      setAiAgentStatus('disconnected')
    }
  }

  // Toggle service mode
  const handleModeChange = async (newMode) => {
    if (!apiService || !apiService.isAuthenticated()) {
      showNotification('Errore: utente non autenticato', 'error')
      return
    }

    try {
      const response = await apiService.request('/api/service/mode', {
        method: 'PUT',
        body: JSON.stringify({ mode: newMode, autoTrading: newMode === 'full-auto' })
      })

      if (response.success) {
        setServiceMode(newMode)
        setAutoTradingEnabled(newMode === 'full-auto')
        
        const modeLabels = {
          'manual': 'Modalità Manuale',
          'ai-assisted': 'Modalità AI-Assistita',
          'full-auto': 'Modalità Completamente Automatica'
        }
        
        showNotification(`Attivata ${modeLabels[newMode]}`, 'success')
        setLastActivity(new Date().toISOString())
      }
    } catch (error) {
      showNotification(`Errore cambio modalità: ${error.message}`, 'error')
    }
  }

  // Start/Stop auto trading
  const toggleAutoTrading = async () => {
    if (!apiService || !apiService.isAuthenticated()) return

    try {
      const newStatus = !autoTradingEnabled
      const response = await apiService.request('/api/service/auto-trading', {
        method: 'PUT',
        body: JSON.stringify({ enabled: newStatus })
      })

      if (response.success) {
        setAutoTradingEnabled(newStatus)
        showNotification(
          `Trading automatico ${newStatus ? 'ATTIVATO' : 'DISATTIVATO'}`, 
          newStatus ? 'success' : 'warning'
        )
      }
    } catch (error) {
      showNotification(`Errore toggle auto-trading: ${error.message}`, 'error')
    }
  }

  // Check status periodically
  useEffect(() => {
    checkServiceStatus()
    const interval = setInterval(checkServiceStatus, 10000) // Check every 10 seconds
    return () => clearInterval(interval)
  }, [apiService])

  const getModeDescription = (mode) => {
    switch (mode) {
      case 'manual':
        return 'Tutte le operazioni richiedono conferma manuale dell\'utente'
      case 'ai-assisted':
        return 'L\'AI fornisce suggerimenti ma l\'utente decide sempre'
      case 'full-auto':
        return 'L\'AI gestisce automaticamente le operazioni di trading'
      default:
        return ''
    }
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'connected': return '#4caf50'
      case 'busy': return '#ff9800'
      case 'disconnected': return '#f44336'
      default: return '#666'
    }
  }

  return (
    <div className="MuiPaper-root MuiCard-root" style={{ padding: '24px', margin: '16px 0' }}>
      <div className="MuiCardHeader-root" style={{ paddingBottom: '16px' }}>
        <div className="MuiCardHeader-content">
          <h2 className="MuiTypography-h5" style={{ margin: 0, color: '#1976d2' }}>
            🎛️ Controllo Servizio & AI Agent
          </h2>
          <p className="MuiTypography-body2" style={{ color: '#666', margin: '8px 0 0 0' }}>
            Gestisci la modalità operativa e l'automazione del sistema
          </p>
        </div>
      </div>

      {/* Status Overview */}
      <div 
        className="MuiPaper-root" 
        style={{ 
          padding: '16px', 
          backgroundColor: '#f8f9fa', 
          borderRadius: '8px', 
          marginBottom: '24px',
          border: '1px solid #e9ecef'
        }}
      >
        <h3 style={{ margin: '0 0 16px 0', color: '#333' }}>Status Sistema</h3>
        
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
          {/* Backend Status */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div 
              style={{ 
                width: '12px', 
                height: '12px', 
                borderRadius: '50%', 
                backgroundColor: isOnline ? '#4caf50' : '#f44336'
              }}
            />
            <span style={{ fontWeight: 500 }}>
              Backend: {isOnline ? 'Online' : 'Offline'}
            </span>
          </div>

          {/* AI Agent Status */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div 
              style={{ 
                width: '12px', 
                height: '12px', 
                borderRadius: '50%', 
                backgroundColor: getStatusColor(aiAgentStatus)
              }}
            />
            <span style={{ fontWeight: 500 }}>
              AI Agent: {aiAgentStatus === 'connected' ? 'Connesso' : 
                        aiAgentStatus === 'busy' ? 'In elaborazione' : 'Disconnesso'}
            </span>
          </div>

          {/* Auto Trading Status */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div 
              style={{ 
                width: '12px', 
                height: '12px', 
                borderRadius: '50%', 
                backgroundColor: autoTradingEnabled ? '#4caf50' : '#666'
              }}
            />
            <span style={{ fontWeight: 500 }}>
              Auto-Trading: {autoTradingEnabled ? 'ATTIVO' : 'Inattivo'}
            </span>
          </div>
        </div>

        {lastActivity && (
          <p style={{ margin: '12px 0 0 0', fontSize: '0.9rem', color: '#666' }}>
            Ultima attività: {new Date(lastActivity).toLocaleString('it-IT')}
          </p>
        )}
      </div>

      {/* Mode Selection */}
      <div style={{ marginBottom: '24px' }}>
        <h3 style={{ margin: '0 0 16px 0', color: '#333' }}>Modalità Operativa</h3>
        
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {/* Manual Mode */}
          <div 
            className="MuiPaper-root" 
            style={{ 
              padding: '16px', 
              border: serviceMode === 'manual' ? '2px solid #1976d2' : '1px solid #e0e0e0',
              borderRadius: '8px',
              backgroundColor: serviceMode === 'manual' ? '#e3f2fd' : 'white',
              cursor: 'pointer'
            }}
            onClick={() => handleModeChange('manual')}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <h4 style={{ margin: '0 0 4px 0', color: '#1976d2' }}>
                  👤 Modalità Manuale
                </h4>
                <p style={{ margin: 0, fontSize: '0.9rem', color: '#666' }}>
                  {getModeDescription('manual')}
                </p>
              </div>
              <div 
                style={{ 
                  width: '20px', 
                  height: '20px', 
                  borderRadius: '50%',
                  border: '2px solid #1976d2',
                  backgroundColor: serviceMode === 'manual' ? '#1976d2' : 'transparent'
                }}
              />
            </div>
          </div>

          {/* AI Assisted Mode */}
          <div 
            className="MuiPaper-root" 
            style={{ 
              padding: '16px', 
              border: serviceMode === 'ai-assisted' ? '2px solid #4caf50' : '1px solid #e0e0e0',
              borderRadius: '8px',
              backgroundColor: serviceMode === 'ai-assisted' ? '#e8f5e9' : 'white',
              cursor: 'pointer'
            }}
            onClick={() => handleModeChange('ai-assisted')}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <h4 style={{ margin: '0 0 4px 0', color: '#4caf50' }}>
                  🤖👤 Modalità AI-Assistita
                </h4>
                <p style={{ margin: 0, fontSize: '0.9rem', color: '#666' }}>
                  {getModeDescription('ai-assisted')}
                </p>
              </div>
              <div 
                style={{ 
                  width: '20px', 
                  height: '20px', 
                  borderRadius: '50%',
                  border: '2px solid #4caf50',
                  backgroundColor: serviceMode === 'ai-assisted' ? '#4caf50' : 'transparent'
                }}
              />
            </div>
          </div>

          {/* Full Auto Mode */}
          <div 
            className="MuiPaper-root" 
            style={{ 
              padding: '16px', 
              border: serviceMode === 'full-auto' ? '2px solid #ff9800' : '1px solid #e0e0e0',
              borderRadius: '8px',
              backgroundColor: serviceMode === 'full-auto' ? '#fff3e0' : 'white',
              cursor: 'pointer'
            }}
            onClick={() => handleModeChange('full-auto')}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <h4 style={{ margin: '0 0 4px 0', color: '#ff9800' }}>
                  🤖 Modalità Completamente Automatica
                </h4>
                <p style={{ margin: 0, fontSize: '0.9rem', color: '#666' }}>
                  {getModeDescription('full-auto')}
                </p>
              </div>
              <div 
                style={{ 
                  width: '20px', 
                  height: '20px', 
                  borderRadius: '50%',
                  border: '2px solid #ff9800',
                  backgroundColor: serviceMode === 'full-auto' ? '#ff9800' : 'transparent'
                }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Auto Trading Controls (only in full-auto mode) */}
      {serviceMode === 'full-auto' && (
        <div 
          className="MuiPaper-root" 
          style={{ 
            padding: '16px', 
            backgroundColor: autoTradingEnabled ? '#e8f5e9' : '#ffeaea', 
            borderRadius: '8px',
            border: `2px solid ${autoTradingEnabled ? '#4caf50' : '#f44336'}`
          }}
        >
          <h3 style={{ margin: '0 0 16px 0', color: autoTradingEnabled ? '#2e7d32' : '#d32f2f' }}>
            ⚡ Controllo Trading Automatico
          </h3>
          
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <p style={{ margin: '0 0 8px 0', fontWeight: 500 }}>
                Status: {autoTradingEnabled ? '🟢 ATTIVO' : '🔴 INATTIVO'}
              </p>
              <p style={{ margin: 0, fontSize: '0.9rem', color: '#666' }}>
                {autoTradingEnabled 
                  ? 'L\'AI sta gestendo automaticamente le operazioni di trading'
                  : 'Trading automatico disattivato - modalità standby'
                }
              </p>
            </div>
            
            <button
              onClick={toggleAutoTrading}
              className="MuiButton-root MuiButton-contained"
              style={{
                backgroundColor: autoTradingEnabled ? '#f44336' : '#4caf50',
                color: 'white',
                padding: '12px 24px',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontWeight: 600,
                fontSize: '1rem'
              }}
            >
              {autoTradingEnabled ? '🛑 STOP AUTO-TRADING' : '🚀 START AUTO-TRADING'}
            </button>
          </div>

          {autoTradingEnabled && (
            <div 
              style={{ 
                marginTop: '16px', 
                padding: '12px', 
                backgroundColor: '#fff3e0',
                borderRadius: '6px',
                border: '1px solid #ff9800'
              }}
            >
              <p style={{ margin: 0, fontSize: '0.9rem', color: '#e65100', fontWeight: 500 }}>
                ⚠️ ATTENZIONE: L'AI Agent sta operando autonomamente. Monitora attentamente le operazioni.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Quick Actions */}
      <div style={{ marginTop: '24px' }}>
        <h3 style={{ margin: '0 0 12px 0', color: '#333' }}>Azioni Rapide</h3>
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          <button
            onClick={checkServiceStatus}
            className="MuiButton-root MuiButton-outlined"
            style={{
              border: '1px solid #1976d2',
              color: '#1976d2',
              padding: '8px 16px',
              borderRadius: '4px',
              background: 'white',
              cursor: 'pointer'
            }}
          >
            🔄 Refresh Status
          </button>
        </div>
      </div>
    </div>
  )
}

export default ServiceModeController
